import { useEffect, useState } from "react";
import { Download, Loader2, Radar } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, formatApiError } from "../../lib/api";

export default function TrendsPage() {
  const [niches, setNiches] = useState([]);
  const [pool, setPool] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [markup, setMarkup] = useState({});
  const [importing, setImporting] = useState(null);

  const load = () => {
    api.get("/admin/trends/niches").then((r) => setNiches(r.data.niches));
    api.get("/admin/trends/import-pool").then((r) => setPool(r.data.pool));
  };
  useEffect(load, []);

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/trends/discover");
      setNiches(data.niches);
      toast.success(`Scan complete — top niche: ${data.top_niche} (MOCKED sources)`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setScanning(false);
  };

  const importProduct = async (poolId) => {
    setImporting(poolId);
    try {
      const { data } = await api.post("/admin/products/import", { pool_id: poolId, markup_percent: Number(markup[poolId] ?? 180) });
      toast.success(`Imported "${data.product.name}" at ${fmt(data.product.price)} (MOCKED AliExpress import)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setImporting(null);
  };

  return (
    <div data-testid="admin-trends-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">Trend discovery</p>
          <h1 className="font-heading text-2xl font-bold text-white">Niches & winning products</h1>
          <p className="mt-1 text-xs text-slate-500">Sources: Google Trends · Amazon Best Sellers · eBay Trending <span className="text-amber-500">(MOCKED — plug in API keys to go live)</span></p>
        </div>
        <button data-testid="run-discovery-btn" onClick={scan} disabled={scanning} className="btn-gold !px-5 !py-2.5 text-sm disabled:opacity-60">
          {scanning ? <Loader2 size={15} className="animate-spin" /> : <Radar size={15} />}
          {scanning ? "Scanning…" : "Run discovery scan"}
        </button>
      </div>

      <div className="card-surface overflow-hidden" data-testid="niches-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/[0.08] text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3">Niche</th>
              <th className="px-5 py-3">Trend score</th>
              <th className="px-5 py-3">Search volume</th>
              <th className="px-5 py-3">Growth 30d</th>
              <th className="px-5 py-3">Signals (GT / AMZ / eBay)</th>
              <th className="px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {niches.map((n) => (
              <tr key={n.name} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`niche-row-${n.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <td className="px-5 py-3 font-medium text-white">{n.name}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-20 overflow-hidden rounded-full bg-white/10">
                      <div className="h-full rounded-full bg-amber-400" style={{ width: `${n.trend_score}%` }} />
                    </div>
                    <span className="font-mono text-xs text-amber-300">{n.trend_score}</span>
                  </div>
                </td>
                <td className="px-5 py-3 text-slate-300">{n.search_volume.toLocaleString()}</td>
                <td className={`px-5 py-3 font-medium ${n.growth_30d >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {n.growth_30d >= 0 ? "+" : ""}{n.growth_30d}%
                </td>
                <td className="px-5 py-3 font-mono text-xs text-slate-400">
                  {n.sources.google_trends} / {n.sources.amazon} / {n.sources.ebay}
                </td>
                <td className="px-5 py-3">
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                    n.status === "breakout" ? "bg-amber-400/15 text-amber-300" : n.status === "rising" ? "bg-sky-400/15 text-sky-300" : "bg-white/5 text-slate-400"
                  }`}>{n.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-10">
        <h2 className="font-heading text-xl font-semibold text-white">AliExpress trending — ready to import</h2>
        <p className="mt-1 text-xs text-slate-500">MOCKED product feed. Set your markup and push straight into the catalog.</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {pool.map((item) => (
            <div key={item.id} className="card-surface overflow-hidden" data-testid={`pool-card-${item.id}`}>
              <img src={item.images[0]} alt={item.name} className="aspect-[4/3] w-full object-cover" />
              <div className="p-4">
                <p className="font-heading text-sm font-semibold text-white">{item.name}</p>
                <p className="mt-1 text-xs text-slate-500">{item.category} · trend {item.trend_score} · <span className="text-emerald-400">+{item.growth_rate}%</span></p>
                <p className="mt-2 text-xs text-slate-400">Supplier cost <span className="font-mono text-white">{fmt(item.cost)}</span></p>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    data-testid={`pool-markup-${item.id}`}
                    type="number"
                    value={markup[item.id] ?? 180}
                    onChange={(e) => setMarkup((m) => ({ ...m, [item.id]: e.target.value }))}
                    className="input-dark !w-20 !px-2 !py-1.5 text-xs"
                  />
                  <span className="text-[11px] text-slate-500">% markup → {fmt(item.cost * (1 + Number(markup[item.id] ?? 180) / 100) - 0.01)}</span>
                </div>
                <button
                  data-testid={`pool-import-${item.id}`}
                  onClick={() => importProduct(item.id)}
                  disabled={importing === item.id}
                  className="btn-gold mt-3 w-full !px-4 !py-2 text-xs disabled:opacity-60"
                >
                  {importing === item.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                  Import to store
                </button>
              </div>
            </div>
          ))}
          {pool.length === 0 && <p className="text-sm text-slate-500" data-testid="pool-empty">All pool products imported. Run another discovery scan later.</p>}
        </div>
      </div>
    </div>
  );
}
